/**
 * Invoice-related strings.
 */
import React from 'react';
import Icons from '../ui/IconButton/Icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ArticleButton from '../ui/ArticleButton/ArticleButton';

const link = 'https://help.outlawpractice.com/en/articles/6134240-importing-transactions-from-lawpay';
const transaction = <b>Transaction</b>;

const HELP_BA = 'Enter a positive amount for a credit, or a negative amount for a debit';
const HELP_S = 'Enter a positive amount to indicate a balance owed, or a negative amount to indicate a refund due';
const transactions = <b>Transactions</b>;
const payment = <b>Payment</b>;
const payments = <b>Payments</b>;
const withdrawal = <b>Withdrawal</b>;
const withdrawals = <b>Withdrawals</b>;
const transfer = <b>Transfer</b>;
const transfers = <b>Transfers</b>;
const adjustment = <b>Adjustment</b>;
const adjustments = <b>Adjustments</b>;
const account = <b>Account</b>;
const trust = <b>Trust</b>;

const bankIcon = <FontAwesomeIcon icon={Icons.BANK.reactIcon} color='green'/>;
const creditIcon = <FontAwesomeIcon icon={Icons.CREDIT_CARD.reactIcon} color='blue'/>;

export default {
    // Transactions
    d_title: 'Transactions',
    h_title: <div>
        <h1>Overview</h1>
        <p>{transactions} consists of {payments}, {withdrawals}, {transfers} and {adjustments}.</p>
        <p>{payments} include funding a trust, making a payment from a trust to operating, and making a payment directly
            from a client to operating.</p>
        <p>{withdrawals} include refunding money from a trust to the client, refunding money from operating to a client,
            and disbursing funds from a trust to a person other than the client.</p>
        <p>{transfers} are simple transfers between client or case trusts for the same client.</p>
        <p>{adjustments} include manual adjustments to a case balance, starting amounts for a trust, or starting balance
            due for a client.</p>
        <h1>Views</h1>
        <p>The {transactions} section has two ledgers: {account} and {trust}. You can toggle between these views by
            making a selection in the dropdown on the right.</p>
        <p>The {account} view shows invoices, adjustments, and payments to balances. This is a quick way to see what the
            current balance is.</p>
        <p>The {trust} view shows all transactions related to client and case trusts. Outlaw Practice supports a
            blend of client and case trust(s) in the same case. For example, if there are two clients, Bob Doe and
            Linda Doe, you will see columns for Bob Doe, Linda Doe, and one for the case trust (which will usually have
            a code like 2022-A-0001).</p>
        <p><ArticleButton article='/guides/financials/transactions/' label='Transactions'/></p>
    </div>,

    d_portal_title: '',
    h_portal_title: '<div><h1>Overview</h1><p><b>Transactions</b> consists of <b>Payments</b>, <b>Withdrawals</b>, <b>Transfers</b> and <b>Adjustments</b>.</p><p><b>Payments</b> include replenishing your trust, making a payment from your trust account to the firm, and making a payment directly to the firm from your checking account or credit card.</p><p><b>Withdrawals</b> include refunds to you from the firm and disbursing funds from your trust account to another person or business.</p><p><b>Transfers</b> are simple transfers between your two of your trusts.  For example, if you have multiple cases with us, we may set up a trust account for each case, or we may use a single general trust that is used for both cases.</p><h1>Account View and Trust Ledgers</h1><p>The <b>Transactions</b> section has two ledgers: <b>Account</b> and <b>Trust</b>. You can toggle between these views by making a selection in the dropdown on the right.</p><p>The <b>Account</b> ledger shows invoices, adjustments, and payments. This shows the rolling balance on your account.  Invoices will be included here, and you can click on them to view or download them.</p><p>The <b>Trust</b> view shows all transactions related to your general and case trusts. </p></div>',

    d_account: 'Account',
    d_accountBalance: <p>Account<br/>Balance</p>,
    d_trustBalance: <p>Trust<br/>Balance</p>,
    d_transactionAmount: <p>Transaction<br/>Amount</p>,
    d_showFailedPayments: 'Show Failed Payments',

    d_errFetchPayment: (params: {name: string, err: string}) => `Could not fetch payment methods from ${params.name}: ${params.err}`,

    d_trustFor: (params: {name: string}) => `Trust for ${params.name}`,

    // TransactionCard
    d_P: 'Payment',
    h_P: 'Record a Payment',
    d_Pmini: 'Pmnt',
    d_Pcode: 'P',

    d_W: 'Withdrawal',
    h_W: 'Record a Withdrawal',
    d_Wmini: 'Wthd',
    d_Wcode: 'W',

    d_T: 'Transfer',
    h_T: 'Transfer Money between Trusts',
    d_Tmini: 'Xfer',
    d_Tcode: 'X',

    d_R: 'Refund',
    d_Rmini: 'Rfnd',
    d_Rcode: 'R',

    d_A: 'Adjustment',
    h_A: 'Manually Adjust a Trust or Account Balance',
    d_Amini: 'Adj',
    d_Acode: 'A',

    d_E: 'Escrow',
    d_Emini: 'Esc',
    d_Ecode: 'E',

    d_F: 'Fund',
    h_F: 'Fund a Trust',
    d_Fmini: 'Fund',
    d_Fcode: 'F',

    d_I: 'Invoice',
    d_Imini: 'Inv',
    d_draftInvoice: 'Draft',
    d_Icode: 'I',

    d_BAmini: 'Bal',
    d_BAcode: 'BA',

    d_STmini: 'Start',
    d_STcode: 'ST',

    d_FR: 'Fund a Trust',
    h_FR: 'Fund a case or client (general) trust.',
    d_PT: 'Make a Payment from Trust',
    h_PT: 'Pay an invoice using funds from a case or client (general) trust.',
    d_PC: 'Take a Payment',
    h_PC: 'Apply a payment received from the client, a third party, or another firm — e.g. credit card or cash to your operating account.',
    d_RR: 'Refund Money from Trust',
    h_RR: 'Refund money from a case or client (general) trust back to the client or benefactor.',
    d_RO: 'Refund Money from Operating',
    h_RO: 'Refund money to a client or benefactor from your Operating account.',
    d_DT: 'Disburse Funds',
    h_DT: 'Disburse funds from a case or client (general) trust to a person or entity (other than the client or their benefactors).',
    d_TT: 'Transfer Money between Trusts',
    h_TT: `Transfer money between a client's case and client (general) trusts accounts.`,
    d_ED: 'Fund Escrow',
    h_ED: 'Deposit money into an escrow account.',
    d_DE: 'Disburse Escrow',
    h_DE: 'Disburse funds from an escrow account.',
    d_BA: 'Manual Adjustment',
    h_BA: 'Manually add a debit or credit to a case balance.',
    d_ST: 'Initial Trust',
    h_ST: <div>
        Manually set the starting amount for a client or case trust.<br/>
        This is used when transferring an existing case into Outlaw Practice.
    </div>,
    d_SA: 'Initial Balance',
    h_SA: <div>
        Manually set the starting amount due for a case.<br/>
        This is used when transferring an existing case into Outlaw Practice.
    </div>,

    d_credit: 'credit',
    d_debit: 'debit',
    d_incomingBalance: 'incoming balance',
    d_leaveEmpty: 'Leave empty for payment from general trust',
    d_balance: 'Balance',
    d_balanceMini: 'Bal',
    d_amount: 'Amount',
    h_amount: (params: {txType?: string}) => params?.txType === 'BA' ? HELP_BA : HELP_S,
    d_amountMini: 'Amt',
    d_receipts: 'Receipts',
    d_receiptsMini: 'Rcpts',
    d_disbursements: 'Disbursements',
    d_disbursementsMini: 'Disb',
    d_depositTo: 'Deposit To',
    h_depositTo: '<div><p>The trust to deposit to, or the operating account.</p></div>',
    d_refused: 'Refused',
    d_voided: 'Voided',
    d_operating: 'Operating Account',
    d_matterTrusts: 'Case Trusts',
    d_generalTrust: 'General Trust',
    d_refSourceError: 'Could not find reference for source',
    d_refTargetError: 'Could not find reference for target',
    d_allCases: 'All Cases',
    d_allCasesFor: (params: PaymentAllCasesFor) => `All cases for ${params.contact}`,
    d_distribution: 'Distribution',
    d_payment: 'Payment',
    d_paymentMethod: 'Payment Method',
    h_paymentMethod: <div>
        <p>The payment method used by the payer.</p>
        <p>A large list of payment methods is listed, or you can choose other and explain the
            method in the Notes or the Reference # fields.</p>
    </div>,

    // User version
    d_payer: 'Payer',
    h_payer: '<div><p>Who made the payment?</p><p>This must be a <b>Client</b> or a <b>Benefactor.</b></p><p><b>Benefactors</b> are added in a Lead using the "Who is paying?" field, or in the Case under the Participants section, using the role "Benefactor".</p><p>Note that when a Lead is converted to a Case, anybody listed in the "Who is paying?" field will be promoted to "Benefactors" in the case.</p></div>',

    // Portal version
    d_payerPortal: 'Name on Card / Account',
    h_payerPortal: 'Whose credit card/eCheck account is this? Select the payer first, then choose or add a credit card or eCheck account in the Credit Card/eCheck field.',

    d_invoice: 'Invoice',
    h_invoice: '<div><p>The invoice containing this transaction. This will only apply once the invoice has been finalized.</p></div>',

    d_invoiceNumber: 'Invoice #',

    d_toAccount: 'To Account',
    h_toAccount: <div>
        <p>Into which account will this transaction be deposited?</p>
        <p>There are three types of accounts, not all applicable to every type of transaction:</p>
        <ul>
            <li>A <b>Case Trust</b> is a trust only for the specified case. It will be identified using the
                case icon along with the the name of the case and the amount in the account, e.g. <FontAwesomeIcon
                    icon={Icons.CASE.reactIcon}
                    color='maroon'/> Trust for Divorce <span style={{color: 'green'}}>[$4,220.00]</span>
            </li>
            <li>A <b>Client Trust</b>, also called a general trust, which provides money for any case related to the
                client. These will be identified using the client icon along with the client name and the amount in the
                trust, e.g. <FontAwesomeIcon icon={Icons.CONTACT.reactIcon} color='blue'/> Trust for John Doe <span
                    style={{color: 'green'}}>[$1,250.00]</span>
            </li>
            <li>The <b>Operating Account</b>, which is your firm's checking account.</li>
        </ul>
    </div>,
    d_trust: 'Trust',
    h_trust: 'Which trust should the payment come from?',
    d_referenceNumber: 'Reference #',
    h_referenceNumber: 'A unique transaction number to identify the transaction, such as a check number or credit card authorization code.',
    d_processOn: 'Process On',
    h_processOn: 'If the transaction should not be processed immediately, this will be the date when the transaction should be processed. For example, a client may not have funds available until payday.',
    d_fundsAvailable: 'Funds Available On',
    h_fundsAvailable: 'Estimated date that the funds will clear the bank. This date can vary based on the day of week, credit card processor or bank.',
    d_refusedOn: 'Refused?',
    h_refusedOn: 'If the payment was refused by the bank, check here and add the date it was refused.',
    d_voidedOn: 'Voided/Refunded?',
    h_voidedOn: 'If the transaction was voided or the money refunded, check here and add the date that it was voided or refunded.',
    d_date: 'Date',
    h_date: '<div><p>The date and time when the transaction occurred (not when it was recorded).</p></div>',
    d_notes: 'Notes',
    h_notes: '<div><p>Add any notes you wish to associate with this entry.</p><p><b>These will be displayed to the client on the invoice!</b></p></div>',
    d_modifyNotice: <div><b><i>Note:</i></b> This transaction is currently locked, because it is included in a finalized
        invoice. You can force removal of this transaction from the invoice, and either make it editable (so that you
        can include it in another invoice), or delete it permanently.
    </div>,
    d_edit: <span>Remove from Finalized Invoice and <b><u>Edit</u></b></span>,
    d_delete: <span>Remove from Finalized Invoice and <b><u>Delete</u></b></span>,
    d_errUnknownChoice: 'Unknown choice: ',
    d_errUnknownTxType: (params: PaymentErrUnknownType) => `Unknown transaction type ${params.type}`,

    // Error ladder
    d_need: (params: PaymentNeed) => `Need a${params.an ? 'n' : ''} ${params.v}`,
    d_doesNotAddUp: 'Does not add up',
    d_insufficientFunds: 'Insufficient Funds',

    // Help Me Decide toolbar button (full / mini / code).
    // The wizard dialog content itself is in HelpMeDecide.tsx, not i18n.
    d_helpMeDecide: 'Help Me Decide',
    d_helpMeDecideMini: 'Help',
    d_helpMeDecideCode: '?',
    d_helpMeDecideTitle: 'Help Me Decide — choose a transaction type',

    // Import
    d_loading: 'Loading Transactions',

    // Cards
    d_lead: 'Lead',
    h_lead: '<div><p>The Lead related to this transaction</p></div>',

    d_clients: 'Client(s)',
    h_clients: 'You many choose any number of clients, but you will only be able to choose cases where all specified contacts are part of the case',

    d_fromAccount: 'From Account',
    h_fromAccount: 'Accounts with sufficient funds for this transaction',

    d_matterIds: 'Case',
    h_matterIds: 'You may specify any number of cases here. You will be prompted for a breakdown of the distribution if necessary.',

    d_recipient: 'Recipient',
    h_recipient: 'Choose a person or company to which you will disburse funds',

    // User version
    d_lawpayCards: (params: PaymentLawpayCards) => `${params.processor} Cards/eCheck`,
    h_lawpayCards: 'Click the button to get a list of LawPay payment methods for the payer.',

    // Portal version
    d_lawpayCardsPortal: 'Credit Cards/eCheck',
    h_lawpayCardsPortal: 'To make a payment, set up a credit card or eCheck account, then select it in the Payment Method field.',

    // WithdrawalCard
    d_wCase: 'Case',
    h_wCase: 'Specify the case you would like to disburse funds from.',
    d_wFromTrust: 'From Trust',
    h_wFromTrust: 'Trust to withdraw funds from for this transaction.',
    d_wPayee: 'Payee',
    h_wPayee: 'Who is the recipient of this payment?',
    d_wVoidedOn: 'Voided?',
    h_wVoidedOn: 'If the transaction was voided, check here and add the date that it was voided.',

    // BalanceAdjustmentCard
    d_helpBA: HELP_BA,
    d_helpS: HELP_S,
    d_bEmptyTrust: 'Leave empty for client trust',
    d_bClient: 'Client',
    h_bClient: 'The client whose trust we want to set the starting balance for.',
    d_bClients: 'Client(s)',
    h_bClients: 'The clients for the case trust we want to adjust.',

    // PaymentActionBox
    d_pmtCaseTrust: 'case trust',
    d_pmtGeneralTrust: 'general trust',
    d_pmtVoided: 'Your payment has been voided by the merchant.',
    d_pmtRefunded: (params: PaymentRefunded) => `Your payment has been ${params.isPartial ? 'partially' : 'fully'} refunded by the merchant.`,
    d_pmtReplenish: (params: PaymentReplenish) => `Thank you for replenishing your ${params.trustName}!`,
    d_pmtFund: (params: PaymentFund) => `Thank you for funding your ${params.trustName}!`,
    d_pmtThankYou: 'Thank you for your payment!',
    d_pmtTxDate: (params: PaymentTxDate) => `${params.txDate} at ${params.txTime}`,
    d_pmtAccountType: (params: PaymentAccountType) => `${params.accountType} account`,
    d_pmtPayerName: (params: PaymentPayerName) => `${params.name}'s`,
    d_pmtForCase: (params: PaymentForCase) => ` for case ${params.code}`,
    d_pmtVoidedCharge: (params: PaymentVoidedCharge) => `On ${params.voidOrRefundOn}, ${params.billingName} voided the charge of ${params.amountFmt} to ${params.payerName} ${params.methodName} ending in ${params.methodNumber}.`,
    d_pmtRefundCharge: (params: PaymentRefundCharge) => `On ${params.voidOrRefundOn}, ${params.billingName} ${params.isPartial ? 'partially' : 'fully'} refunded ${params.amountFmt} to ${params.payerName} ${params.methodName} ending in ${params.methodNumber}.`,
    d_pmtChargedOn: (params: PaymentChargedOn) => `On ${params.paymentDate}, ${params.billingName} charged ${params.amountFmt} to ${params.payerName} ${params.methodName} ending in ${params.methodNumber}.`,
    d_pmtDeposited: (params: PaymentDeposited) => `These funds were deposited into your ${params.trustName}${params.issueName}.`,
    d_pmtAppliedTo: `These funds were applied to your outstanding account balance.`,
    d_pmtConsultName: (params: PaymentConsultName) => `These funds paid for your ${params.consultName}.`,
    d_pmtNotes: (params: PaymentNotes) => `Note from ${params.billingName}: ${params.comment}`,
    d_pmtThankYouBusiness: 'Thank you for your business!',
    d_pmtHtmlSubject: (params: PaymentHtmlSubject) => `<p><b>${params.subject}</b></p>`,
    d_pmtHtmlNotes: (params: PaymentNotes) => `<p>Note from ${params.billingName}:<br/><i>${params.comment}</i></p>`,
    d_pmtConfigure: 'Configure',
    d_pmtConfigurePayments: 'Configure Payments',
    d_pmtAuthCode: 'Authorization code is',
    d_pmtEmails: 'Emails will be sent to the following addresses:',
    d_pmtEmailsSent: 'Emails were sent to the following addresses:',
    d_pmtProcess: 'Process Payment',
    d_pmtProcess_portal: 'Pay',
    d_pmtVoid: 'Void',
    d_pmtRefund: 'Refund',
    d_pmtPaymentId: 'Payment ID is',
    d_pmtProcessorPaymentUser: 'Payment taken by'
};

export type PaymentAllCasesFor = {
    contact: string
}

export type PaymentErrUnknownType = {
    type: string
}

export type PaymentNeed = {
    an: boolean,
    v: string
}

export type PaymentLawpayCards = {
    processor: string
}

export type PaymentRefunded = {
    isPartial: boolean
}

export type PaymentReplenish = {
    trustName: string
}

export type PaymentFund = {
    trustName: string
}

export type PaymentTxDate = {
    txDate: string,
    txTime: string
}

export type PaymentAccountType = {
    accountType: string
}

export type PaymentPayerName = {
    name: string
}

export type PaymentForCase = {
    code: string
}

export type PaymentVoidedCharge = {
    voidOrRefundOn: string,
    amountFmt: string,
    methodName: string,
    methodNumber: string,
    billingName: string,
    payerName: string
}

export type PaymentRefundCharge = {
    voidOrRefundOn: string,
    isPartial: boolean,
    amountFmt: string,
    methodName: string,
    methodNumber: string,
    billingName: string,
    payerName: string
}

export type PaymentChargedOn = {
    paymentDate: string,
    amountFmt: string,
    methodName: string,
    methodNumber: string,
    billingName: string,
    payerName: string
}

export type PaymentDeposited = {
    trustName: string,
    issueName: string
}

export type PaymentConsultName = {
    consultName: string
}

export type PaymentNotes = {
    billingName: string,
    comment: string
}

export type PaymentHtmlSubject = {
    subject: string
}
